import React, { useState, useEffect } from 'react';
import { Plus, Trash2, Edit2, ExternalLink, ArrowRight, Check, X } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Switch } from '../components/ui/switch';
import { toast } from 'sonner';
import api from '../utils/api';
import Sidebar from '../components/Sidebar';

const Redirects = () => {
  const [redirects, setRedirects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({
    source_path: '',
    target_path: '',
    redirect_type: '301',
    is_active: true
  });

  useEffect(() => {
    fetchRedirects();
  }, []);

  const fetchRedirects = async () => {
    try {
      const response = await api.get('/redirects');
      setRedirects(response.data);
    } catch (error) {
      toast.error('Failed to load redirects');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.source_path || !formData.target_path) {
      toast.error('Please fill in both paths');
      return;
    }

    try {
      if (editingId) {
        await api.put(`/redirects/${editingId}`, formData);
        toast.success('Redirect updated');
      } else {
        await api.post('/redirects', formData);
        toast.success('Redirect created');
      }
      
      fetchRedirects();
      resetForm();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save redirect');
    }
  };

  const handleDelete = async (redirectId) => {
    if (!window.confirm('Are you sure you want to delete this redirect?')) {
      return;
    }

    try {
      await api.delete(`/redirects/${redirectId}`);
      toast.success('Redirect deleted');
      fetchRedirects();
    } catch (error) {
      toast.error('Failed to delete redirect');
    }
  };

  const handleEdit = (redirect) => {
    setEditingId(redirect.redirect_id);
    setFormData({
      source_path: redirect.source_path,
      target_path: redirect.target_path,
      redirect_type: redirect.redirect_type,
      is_active: redirect.is_active
    });
    setShowAddForm(true);
  };

  const toggleActive = async (redirect) => {
    try {
      await api.put(`/redirects/${redirect.redirect_id}`, {
        is_active: !redirect.is_active
      });
      fetchRedirects();
    } catch (error) {
      toast.error('Failed to update redirect');
    }
  };

  const resetForm = () => {
    setFormData({
      source_path: '',
      target_path: '',
      redirect_type: '301',
      is_active: true
    });
    setEditingId(null);
    setShowAddForm(false);
  };

  return (
    <div className="flex min-h-screen bg-slate-50">
      <Sidebar />
      <div className="flex-1 min-w-0 p-8">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="flex items-center justify-between mb-8">
            <div>
              <h1 className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
                URL Redirects
              </h1>
              <p className="text-slate-500 mt-1">
                Manage redirects to preserve SEO and fix broken links
              </p>
            </div>
            <Button 
              onClick={() => setShowAddForm(!showAddForm)}
              className="bg-indigo-600 hover:bg-indigo-700"
              data-testid="add-redirect-button"
            >
              <Plus size={18} className="mr-2" />
              Add Redirect
            </Button>
          </div>

          {/* Add/Edit Form */}
          {showAddForm && (
            <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6 mb-6">
              <h2 className="text-lg font-semibold text-slate-900 mb-4">
                {editingId ? 'Edit Redirect' : 'Add New Redirect'}
              </h2>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="source_path">Source Path (old URL)</Label>
                    <div className="flex items-center mt-1">
                      <span className="text-slate-500 mr-1">/</span>
                      <Input
                        id="source_path"
                        value={formData.source_path.replace(/^\//, '')}
                        onChange={(e) => setFormData({ ...formData, source_path: e.target.value })}
                        placeholder="old-page-url"
                        data-testid="source-path-input"
                      />
                    </div>
                    <p className="text-xs text-slate-500 mt-1">The URL path that should redirect</p>
                  </div>
                  <div>
                    <Label htmlFor="target_path">Target Path (new URL)</Label>
                    <div className="flex items-center mt-1">
                      <span className="text-slate-500 mr-1">/</span>
                      <Input
                        id="target_path"
                        value={formData.target_path.replace(/^\//, '')}
                        onChange={(e) => setFormData({ ...formData, target_path: e.target.value })}
                        placeholder="new-page-url"
                        data-testid="target-path-input"
                      />
                    </div>
                    <p className="text-xs text-slate-500 mt-1">Where visitors should be redirected to</p>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="redirect_type">Redirect Type</Label>
                    <Select 
                      value={formData.redirect_type} 
                      onValueChange={(value) => setFormData({ ...formData, redirect_type: value })}
                    >
                      <SelectTrigger className="mt-1">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="301">301 - Permanent (recommended for SEO)</SelectItem>
                        <SelectItem value="302">302 - Temporary</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex items-center gap-3 pt-6">
                    <Switch
                      checked={formData.is_active}
                      onCheckedChange={(checked) => setFormData({ ...formData, is_active: checked })}
                    />
                    <Label>Active</Label>
                  </div>
                </div>

                <div className="flex gap-3 pt-2">
                  <Button type="submit" className="bg-indigo-600 hover:bg-indigo-700">
                    {editingId ? 'Update Redirect' : 'Create Redirect'}
                  </Button>
                  <Button type="button" variant="outline" onClick={resetForm}>
                    Cancel
                  </Button>
                </div>
              </form>
            </div>
          )}

          {/* Redirects List */}
          <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
            {loading ? (
              <div className="p-8 text-center text-slate-500">Loading...</div>
            ) : redirects.length === 0 ? (
              <div className="p-8 text-center">
                <ExternalLink size={48} className="mx-auto text-slate-300 mb-4" />
                <h3 className="text-lg font-medium text-slate-700 mb-2">No redirects yet</h3>
                <p className="text-slate-500 mb-4">
                  Create redirects to preserve old URLs when you restructure your site
                </p>
                <Button 
                  onClick={() => setShowAddForm(true)}
                  className="bg-indigo-600 hover:bg-indigo-700"
                >
                  <Plus size={18} className="mr-2" />
                  Add Your First Redirect
                </Button>
              </div>
            ) : (
              <table className="w-full">
                <thead className="bg-slate-50 border-b border-slate-200">
                  <tr>
                    <th className="text-left p-4 text-sm font-medium text-slate-600">Source</th>
                    <th className="text-left p-4 text-sm font-medium text-slate-600"></th>
                    <th className="text-left p-4 text-sm font-medium text-slate-600">Target</th>
                    <th className="text-center p-4 text-sm font-medium text-slate-600">Type</th>
                    <th className="text-center p-4 text-sm font-medium text-slate-600">Status</th>
                    <th className="text-right p-4 text-sm font-medium text-slate-600">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {redirects.map((redirect) => (
                    <tr key={redirect.redirect_id} className="border-b border-slate-100 hover:bg-slate-50">
                      <td className="p-4">
                        <code className="text-sm bg-slate-100 px-2 py-1 rounded text-slate-700">
                          {redirect.source_path}
                        </code>
                      </td>
                      <td className="p-4">
                        <ArrowRight size={16} className="text-slate-400" />
                      </td>
                      <td className="p-4">
                        <code className="text-sm bg-emerald-50 px-2 py-1 rounded text-emerald-700">
                          {redirect.target_path}
                        </code>
                      </td>
                      <td className="p-4 text-center">
                        <span className={`text-xs px-2 py-1 rounded-full ${
                          redirect.redirect_type === '301' 
                            ? 'bg-blue-100 text-blue-700' 
                            : 'bg-amber-100 text-amber-700'
                        }`}>
                          {redirect.redirect_type}
                        </span>
                      </td>
                      <td className="p-4 text-center">
                        <button
                          onClick={() => toggleActive(redirect)}
                          className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs ${
                            redirect.is_active 
                              ? 'bg-green-100 text-green-700' 
                              : 'bg-slate-100 text-slate-500'
                          }`}
                        >
                          {redirect.is_active ? (
                            <>
                              <Check size={12} />
                              Active
                            </>
                          ) : (
                            <>
                              <X size={12} />
                              Inactive
                            </>
                          )}
                        </button>
                      </td>
                      <td className="p-4 text-right">
                        <div className="flex items-center justify-end gap-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleEdit(redirect)}
                          >
                            <Edit2 size={16} />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDelete(redirect.redirect_id)}
                            className="text-red-500 hover:text-red-700 hover:bg-red-50"
                          >
                            <Trash2 size={16} />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          {/* Info Box */}
          <div className="mt-6 p-4 bg-blue-50 rounded-lg border border-blue-100">
            <h3 className="font-medium text-blue-900 mb-2">About Redirects</h3>
            <ul className="text-sm text-blue-700 space-y-1">
              <li>• <strong>301 Permanent:</strong> Use when a page has permanently moved. Search engines will update their index.</li>
              <li>• <strong>302 Temporary:</strong> Use for temporary redirects. Search engines keep the old URL in their index.</li>
              <li>• Redirects help preserve SEO value when restructuring your site.</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Redirects;
